import React, { useState, useEffect } from 'react';
import { Button, Snackbar, Alert, Box, Typography, LinearProgress } from '@mui/material';
import { Refresh, Close, Pause } from '@mui/icons-material';
import versionService from '../services/versionService';

const UpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  useEffect(() => {
    // Listen for version updates
    const handleVersionUpdate = (hasUpdate) => {
      if (hasUpdate) {
        setShowUpdate(true);
        setCountdown(3); // Reset countdown
        setAutoRefreshEnabled(true);
      }
    };

    versionService.addListener(handleVersionUpdate);

    // Cleanup listener on unmount
    return () => {
      versionService.removeListener(handleVersionUpdate);
    };
  }, []);

  // Countdown effect
  useEffect(() => {
    if (showUpdate && autoRefreshEnabled && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showUpdate && autoRefreshEnabled && countdown === 0) {
      // Auto-refresh when countdown reaches 0
      handleRefresh();
    }
  }, [showUpdate, autoRefreshEnabled, countdown]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Small delay to show the refreshing state
    setTimeout(() => {
      versionService.forceRefresh();
    }, 500);
  };

  const handleClose = () => {
    setShowUpdate(false);
    setAutoRefreshEnabled(false);
    versionService.setAutoRefresh(false);
  };

  const handleCancelAutoRefresh = () => {
    setAutoRefreshEnabled(false);
    versionService.setAutoRefresh(false);
  };

  return (
    <Snackbar
      open={showUpdate}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        '& .MuiSnackbarContent-root': {
          backgroundColor: '#1976d2',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }
      }}
    >
      <Alert
        severity="info"
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {autoRefreshEnabled && countdown > 0 && (
              <Button
                color="inherit"
                size="small"
                onClick={handleCancelAutoRefresh}
                startIcon={<Pause />}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.5)',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  }
                }}
                variant="outlined"
              >
                Cancel ({countdown}s)
              </Button>
            )}
            <Button
              color="inherit"
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              startIcon={<Refresh />}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                }
              }}
              variant="outlined"
            >
              {isRefreshing ? 'Refreshing...' : 'Update Now'}
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={handleClose}
              sx={{ minWidth: 'auto', padding: '4px' }}
            >
              <Close fontSize="small" />
            </Button>
          </Box>
        }
        sx={{
          backgroundColor: 'transparent',
          color: 'white',
          '& .MuiAlert-message': {
            flex: 1,
          }
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            🚀 New Update Available!
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {autoRefreshEnabled && countdown > 0 
              ? `Auto-updating in ${countdown} seconds... Click "Cancel" to stop.`
              : 'A new version of the app is ready. Click "Update Now" to get the latest features and improvements.'
            }
          </Typography>
          {autoRefreshEnabled && countdown > 0 && (
            <LinearProgress 
              variant="determinate" 
              value={(3 - countdown) / 3 * 100}
              sx={{ 
                mt: 1, 
                height: 2, 
                borderRadius: 1,
                backgroundColor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'white'
                }
              }} 
            />
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default UpdateNotification;
